// react
import React, { useEffect, useState } from 'react';
import axios from 'axios';

// third-party
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

// application
import PageHeader from '../shared/PageHeader';
import Product from '../shared/Product';
import ProductTabs from './ProductTabs';
import shopApi from '../../api/shop';
import { url, slugify } from '../../services/utils';

// blocks
import BlockProductsCarousel from '../blocks/BlockProductsCarousel';
import ProductDetailsSkeleton from '../shared/ProductDetailsSkeleton';

// widgets
import WidgetCategories from '../widgets/WidgetCategories';
import WidgetProducts from '../widgets/WidgetProducts';

// data stubs
import categories from '../../data/shopWidgetCategories';
import theme from '../../data/theme';

function ShopPageProduct(props) {
    const { productSlug, layout, sidebarPosition, description, location, history } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [relatedProducts, setRelatedProducts] = useState([]);
    const [latestProducts, setLatestProducts] = useState([]);

    // Load product.
    useEffect(() => {
        let canceled = false;
        setIsLoading(true);

        const getProduct = async () => {
            try {
                let targetId = location?.state?.productId;

                // 1. If productSlug is already numeric ID (e.g. /shop/products/73312)
                if (!targetId && /^\d+$/.test(productSlug)) {
                    targetId = productSlug;
                }

                // 2. Check cached mapping in sessionStorage or localStorage
                if (!targetId && typeof window !== 'undefined') {
                    targetId =
                        window.sessionStorage?.getItem(`prod_slug_${productSlug}`) ||
                        window.localStorage?.getItem(`prod_slug_${productSlug}`);
                }

                // 3. Fallback: Search by keywords if direct URL was opened in a fresh tab
                if (!targetId) {
                    const searchKeywords = productSlug
                        .split('-')
                        .filter(Boolean)
                        .slice(0, 4)
                        .join(' ');

                    if (searchKeywords) {
                        const searchRes = await axios.post(
                            `${process.env.REACT_APP_HOST_API}/items-searching/page`,
                            { search: searchKeywords, limit: 20, page: 1 }
                        );
                        const items = searchRes.data?.response || [];
                        const matched =
                            items.find((it) => slugify(it.item_name) === productSlug) ||
                            items[0];
                        if (matched) {
                            targetId = matched.id;
                        }
                    }
                }

                if (!targetId) {
                    if (!canceled) setIsLoading(false);
                    return;
                }

                const result = await axios.get(
                    `${process.env.REACT_APP_HOST_API}/items-searching/get/${targetId}`
                );

                if (canceled) return;

                const productData = result.data?.response;
                setProduct(productData);

                if (productData) {
                    const actualId = productData.id;
                    const cleanSlug = slugify(productData.item_name);
                    if (cleanSlug) {
                        try {
                            window.sessionStorage?.setItem(`prod_slug_${cleanSlug}`, String(actualId));
                            window.localStorage?.setItem(`prod_slug_${cleanSlug}`, String(actualId));
                        } catch (e) { }

                        // If user opened with numeric ID in URL, update URL bar to the clean product name
                        if (/^\d+$/.test(productSlug) && history) {
                            history.replace({
                                pathname: `/shop/products/${cleanSlug}`,
                                state: { productId: actualId },
                            });
                        }
                    }
                }

                setIsLoading(false);
            } catch (error) {
                console.error(error);
                if (!canceled) {
                    setIsLoading(false);
                }
            }
        };

        getProduct();

        return () => {
            canceled = true;
        };
    }, [productSlug, location, history]);

    // Load related products.
    useEffect(() => {
        let canceled = false;

        shopApi.getRelatedProducts(productSlug, { limit: 8 }).then((products) => {
            if (canceled) {
                return;
            }
            // console.log(products);
            setRelatedProducts(products.data.response);
        });

        return () => {
            canceled = true;
        };
    }, [productSlug, setRelatedProducts]);

    // Load latest products.
    useEffect(() => {
        let canceled = false;

        if (layout !== 'sidebar') {
            setLatestProducts([]);
        } else {
            shopApi.getLatestProducts({ limit: 5 }).then((result) => {
                if (canceled) {
                    return;
                }
                setLatestProducts(result);
            });
        }

        return () => {
            canceled = true;
        };
    }, [layout]);

    if (isLoading || !product) {
        return (
            <React.Fragment>
                <PageHeader breadcrumb={[{ title: 'Home', url: url.home() }, { title: 'Shop', url: url.catalog() }, { title: '...' }]} />
                <div className="container">
                    <ProductDetailsSkeleton />
                </div>
            </React.Fragment>
        );
    }

    const breadcrumb = [
        { title: 'Home', url: url.home() },
        { title: 'Shop', url: url.catalog() },
        { title: product.item_name, url: url.product(product) },
    ];

    let content;

    if (layout === 'sidebar') {
        const sidebar = (
            <div className="shop-layout__sidebar">
                <div className="block block-sidebar">
                    <div className="block-sidebar__item">
                        <WidgetCategories categories={categories} location="shop" />
                    </div>
                    <div className="block-sidebar__item d-none d-lg-block">
                        <WidgetProducts title="Latest Products" products={latestProducts} />
                    </div>
                </div>
            </div>
        );

        content = (
            <div className="container">
                <div className={`shop-layout shop-layout--sidebar--${sidebarPosition}`}>
                    {sidebarPosition === 'start' && sidebar}
                    <div className=" shop-layout__content">
                        <div className="block block--product-details">
                            <Product product={product} layout={layout} />
                            <ProductTabs description={product} withSidebar />
                        </div>

                        {relatedProducts.length > 0 && (
                            <BlockProductsCarousel
                                title="Rekomendasi Produk"
                                // layout="grid-4-sm"
                                layout="grid-5"
                                rows={2}
                                products={relatedProducts}
                                withSidebar
                            />
                        )}
                    </div>
                    {sidebarPosition === 'end' && sidebar}
                </div>
            </div>
        );
    } else {
        content = (
            <React.Fragment>
                <div className="block block--product-details">
                    <div className="container">
                        <Product product={product} layout={layout} />
                        {/* <ProductTabs /> */}
                    </div>
                </div>

                {relatedProducts.length > 0 && (
                    <BlockProductsCarousel
                        title="Rekomendasi Produk"
                        // layout="grid-5"
                        layout="grid-4"
                        rows={2}
                        products={relatedProducts}
                    />
                )}
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{`${(product && product.item_name) || "Detail Produk"} | ${theme.name}`}</title>
            </Helmet>

            <PageHeader breadcrumb={breadcrumb} />

            {content}
        </React.Fragment>
    );
}

ShopPageProduct.propTypes = {
    /** Product slug. */
    productSlug: PropTypes.string,
    /** one of ['standard', 'sidebar', 'columnar', 'quickview'] (default: 'standard') */
    layout: PropTypes.oneOf(['standard', 'sidebar', 'columnar', 'quickview']),
    /**
     * sidebar position (default: 'start')
     * one of ['start', 'end']
     * for LTR scripts "start" is "left" and "end" is "right"
     */
    sidebarPosition: PropTypes.oneOf(['start', 'end']),
};

ShopPageProduct.defaultProps = {
    layout: 'standard',
    sidebarPosition: 'start',
};

export default ShopPageProduct;
